import { useVersions } from "@/components/InOrganizationProvider/VersionsProvider";
import { useReferencedSheets } from "@/hooks/useReferencedSheets";
import {
  Dropdown,
  Option,
  OptionOnSelectData,
  makeStyles,
} from "@fluentui/react-components";
import { DocumentTableRegular } from "@fluentui/react-icons";
import React, { useCallback, useMemo } from "react";
import { useAppRouteParams } from "../../../AppRoutes";
import { Sheet } from "../../../domain/Sheet";
import { DragAndDropUploadData } from "../../DragAndDropFields/index";
import styles from "./index.module.css";

const useStyles = makeStyles({
  root: { width: "260px" },
  truncatedText: {
    overflowX: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  optionText: {
    overflowX: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
});

type SheetOption = {
  key: string;
  text: string;
  disabled?: boolean;
};

type writeStatus = "waiting" | "in_progress" | "success" | "failed";

type SheetCSVConnect = {
  fileId: string;
  sheetId: string;
  writeStatus: writeStatus;
};

export function CSVConnectItem({
  displaySortedSheets,
  fileId,
  uploadedFileList,
  csvSheetConnectList,
  setCSVSheetConnectList,
}: {
  displaySortedSheets: Sheet[];
  fileId: string;
  uploadedFileList: DragAndDropUploadData[];
  csvSheetConnectList: SheetCSVConnect[];
  setCSVSheetConnectList: React.Dispatch<
    React.SetStateAction<SheetCSVConnect[]>
  >;
}) {
  const dropdownStyles = useStyles();

  const { versionId } = useAppRouteParams();

  const [referencedSheets] = useReferencedSheets();
  const { versions } = useVersions();

  const currentStatus = csvSheetConnectList.find(
    ({ fileId: targetFileId }) => fileId === targetFileId
  )?.writeStatus;

  const sheetDropdownOptions: SheetOption[] = useMemo(
    () =>
      displaySortedSheets.map((sheet) => {
        const referencedSheet = referencedSheets.find(
          (refSheet) =>
            refSheet.id === sheet.referTo?.sheetId &&
            refSheet.refVersionId === sheet.referTo?.versionId
        );
        const referencedVersionName =
          versions.find((v) => v.id === referencedSheet?.refVersionId)?.title ??
          "不明なバージョン";

        return {
          key: sheet.id,
          text: referencedSheet
            ? `${sheet.displayName} (${referencedVersionName})`
            : `${sheet.displayName}`,
          // TADS参照シートは参照シートと同じく自前の行を持たないので紐付けさせない
          disabled:
            csvSheetConnectList.find(({ sheetId }) => sheetId === sheet.id) !==
              undefined ||
            !!referencedSheet ||
            sheet.tadReferTo !== null,
        };
      }),
    [displaySortedSheets, csvSheetConnectList, referencedSheets]
  );

  const currentSelectedOption = csvSheetConnectList.find(
    ({ fileId: targetFileId }) => fileId === targetFileId
  );
  const currentOptionText = displaySortedSheets.find(
    ({ id: targetSheetId }) => targetSheetId === currentSelectedOption?.sheetId
  )?.displayName;

  const isDisableDropdown = currentStatus && currentStatus !== "waiting";

  const onChangeCSVSheetConnectDropdownOption = useCallback(
    (fileId: string, selectedOption: OptionOnSelectData) => {
      const sheetId = selectedOption.optionValue;
      if (!sheetId) return;
      const isExist = csvSheetConnectList.find(
        ({ sheetId: targetSheetId }) => sheetId === targetSheetId
      );
      setCSVSheetConnectList(
        isExist
          ? csvSheetConnectList
          : [
              ...csvSheetConnectList.filter(
                ({ fileId: targetFileId }) => fileId !== targetFileId
              ),
              { fileId, sheetId, writeStatus: "waiting" },
            ]
      );
    },
    [csvSheetConnectList]
  );

  return (
    <div className={styles.file}>
      <DocumentTableRegular style={{ width: 20, height: 20 }} />
      <div className={styles.subtitle}>シート名</div>
      <Dropdown
        selectedOptions={
          currentSelectedOption?.sheetId ? [currentSelectedOption.sheetId] : []
        }
        onOptionSelect={(_, data) => {
          onChangeCSVSheetConnectDropdownOption(fileId, data);
        }}
        button={
          <span
            className={dropdownStyles.truncatedText}
            style={{
              color: currentOptionText
                ? "var(--figma-color-innerText)"
                : "var(--figma-color-sideMenuText-stress)",
              opacity: isDisableDropdown ? 0.2 : 1,
            }}
          >
            {currentOptionText ?? "シートを選択してください"}
          </span>
        }
        root={{ className: dropdownStyles.root }}
        disabled={isDisableDropdown}
      >
        {sheetDropdownOptions.map((opt) => (
          <Option
            key={opt.key}
            value={opt.key}
            text={opt.text}
            disabled={opt.disabled}
          >
            <span className={dropdownStyles.optionText}>{opt.text}</span>
          </Option>
        ))}
      </Dropdown>
    </div>
  );
}
